// Pure mapping of advisor RFZO request failures (POST /rfzo_therapy) into
// friendly, user-facing messages. Extracted from React for unit testing.
//
// The endpoint has two kinds of 400 (apoteka-back be3a1df6):
//   - the duplicate: JSON {errorCode: "RFZO_THERAPY_ALREADY_EXISTS", message};
//   - everything raised as InvalidRequestException (no identifier, malformed
//     JMBG): a bare plain-text sentence with no errorCode and no field key.
// The controller has no @Valid, so a bean-validation field map never arrives.

export interface MappedRfzoRequestError {
  message: string;
}

const readErrorCode = (data: unknown): string | null => {
  if (
    data &&
    typeof data === "object" &&
    typeof (data as { errorCode?: unknown }).errorCode === "string"
  ) {
    return (data as { errorCode: string }).errorCode;
  }
  return null;
};

export const mapRfzoRequestError = (
  error: unknown,
  context: { lbo: string }
): MappedRfzoRequestError => {
  const err = error as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (readErrorCode(data) === "RFZO_THERAPY_ALREADY_EXISTS") {
    // ITAM-2716: the LBO is short and already visible in the form, so naming
    // it helps the advisor confirm they typed the right patient. A JMBG-only
    // draft gets the neutral wording instead: the JMBG is a national
    // identifier and is never echoed back. Honest dead end: no recovery
    // affordance.
    return {
      message: context.lbo
        ? `Za ovog korisnika (LBO ${context.lbo}) već postoji RFZO terapija u ovoj apoteci. Novi upit ne može da se pošalje.`
        : "Za ovog korisnika već postoji RFZO terapija u ovoj apoteci. Novi upit ne može da se pošalje.",
    };
  }

  if (status === 400) {
    // ITAM-2716: the identifier rejections are a bare Serbian sentence, not
    // JSON: InvalidRequestException returns ex.getMessage() and
    // StringHttpMessageConverter outranks Jackson for a String body. The text
    // is the only information the server sent, so it is surfaced verbatim.
    if (typeof data === "string" && data.trim()) {
      return { message: data.trim() };
    }
    return {
      message: "Neispravni podaci RFZO upita. Proverite unos i pokušajte ponovo.",
    };
  }

  if (status != null && status >= 500) {
    return { message: "Došlo je do greške na serveru. Pokušajte ponovo." };
  }

  return {
    message:
      err?.message || "Neuspešno slanje RFZO upita. Pokušajte ponovo.",
  };
};
